import { ThunkAction, ThunkDispatch } from "redux-thunk";
import * as uuidv4 from "uuid/v4";

import { NOTE_SIZE, NoteColor, NoteType, ProcessStep, } from "../constants";
import {
    getCurrentTemplate,
    getNoteDialogState,
    getNotes,
    getPaneState,
    getPresentationId,
    getProcessStep, getZoomLevel,
    State
} from '../reducers';
import { createNote, deleteNote, updateNote } from "../services/presentation-store";
import { Undoable } from "./undo";
import { toCanvasX, toCanvasY } from "../services/template-service";
import { reportError, ReportErrorAction } from "./ui-state";


export interface ShowUpdateNoteDialogAction {
    readonly type: 'SHOW_UPDATE_NOTE_DIALOG',
    readonly uuid: string,
    readonly text: string,
    readonly color: NoteColor,
}

export interface ShowCreateNoteDialogAction {
    readonly type: 'SHOW_CREATE_NOTE_DIALOG',
    readonly color: NoteColor,
}

export interface CloseNoteDialogAction {
    readonly type: 'CLOSE_NOTE_DIALOG',
}

export interface CreateNoteAction extends Undoable {
    readonly type: 'CREATE_NOTE',
    readonly uuid: string,
    readonly text: string,
    readonly color: NoteColor,
    readonly noteType: string,
    readonly noteX: number,
    readonly noteY: number,
}

export interface UpdateNoteAction extends Undoable {
    readonly type: 'UPDATE_NOTE',
    readonly uuid: string,
    readonly oldText: string,
    readonly newText: string,
    readonly oldColor: NoteColor,
    readonly newColor: NoteColor,
}

type SubmitDialogAction = CloseNoteDialogAction | CreateNoteAction | UpdateNoteAction | ReportErrorAction;

export interface TextAreaChangedAction {
    readonly type: 'TEXTAREA_CHANGED',
    readonly text: string,
}

export interface ColorChangedAction {
    readonly type: 'COLOR_CHANGED',
    readonly color: NoteColor,
}


export function showUpdateNoteDialog(uuid: string, text: string, color: NoteColor): ShowUpdateNoteDialogAction {
    return {
        type: 'SHOW_UPDATE_NOTE_DIALOG',
        uuid,
        text,
        color,
    };
}

export function showCreateNoteDialog(color: NoteColor): ShowCreateNoteDialogAction {
    return {
        type: 'SHOW_CREATE_NOTE_DIALOG',
        color,
    };
}

export function cancelNoteDialog(): CloseNoteDialogAction {
    return {
        type: 'CLOSE_NOTE_DIALOG',
    };
}

function getRandomType() {
    switch (Math.floor(Math.random() * 4)) {
        default:
            return NoteType.T0;
        case 1:
            return NoteType.T1;
        case 2:
            return NoteType.T2;
        case 3:
            return NoteType.T3;
    }
}

export function submitDialog(uuid: string, newText: string, newColor: NoteColor): ThunkAction<any, State, any, SubmitDialogAction> {
    return async (dispatch: ThunkDispatch<State, any, SubmitDialogAction>, getState: () => State): Promise<any> => {
        const state = getState();
        if (! getNoteDialogState(state).open) {
            return;
        }
        const presentationId = getPresentationId(state);
        dispatch({
            type: 'CLOSE_NOTE_DIALOG',
        });
        if (uuid) {
            const note = getNotes(state).find(element => element.uuid === uuid);
            if (note) {
                const oldText = note.text;
                const oldColor = note.color;
                const redoEffect = async () => await updateNote(presentationId, uuid, newText, newColor)
                    .catch(error => dispatch(reportError(error)));
                const undoEffect = async () => await updateNote(presentationId, uuid, oldText, oldColor)
                    .catch(error => dispatch(reportError(error)));
                dispatch({
                    type: 'UPDATE_NOTE',
                    uuid: note.uuid,
                    oldText,
                    newText,
                    oldColor,
                    newColor,
                    redoEffect,
                    undoEffect,
                });
                await redoEffect();
            }
        } else {
            const isStoryboard = getProcessStep(state) === ProcessStep.SKETCH;
            const template = getCurrentTemplate(state);
            const width = isStoryboard? template.storyboardWidth : template.canvasWidth;
            const height = isStoryboard? template.storyboardHeight : template.canvasHeight;
            const { scrollLeft, scrollTop, paneWidth, paneHeight } = getPaneState(state);
            const canvasRatio = width / height;
            const zoomLevel = getZoomLevel(state);

            const gapLeft = Math.max(0, zoomLevel * (paneWidth - canvasRatio * paneHeight) / 2);
            const posX = scrollLeft - gapLeft + paneWidth / 2;
            const pixelWidth = zoomLevel * paneWidth - 2 * gapLeft;
            const varianceX = Math.min(3 * NOTE_SIZE, paneWidth / 2 * width / pixelWidth);
            const randomX = Math.random() * varianceX - varianceX / 2 - NOTE_SIZE / 2;
            const rawNoteX = isStoryboard?
                toCanvasX(template, width * posX / pixelWidth + randomX)
                : width * posX / pixelWidth + randomX;
            const noteX = Math.max(-NOTE_SIZE/2, Math.min(rawNoteX, width - NOTE_SIZE/2));

            const gapTop = Math.max(0, zoomLevel * (paneHeight - paneWidth / canvasRatio) / 2);
            const posY = scrollTop - gapTop + paneHeight / 2;
            const pixelHeight = zoomLevel * paneHeight - 2 * gapTop;
            const varianceY = Math.min(3 * NOTE_SIZE, paneHeight / 2 * height / pixelHeight);
            const randomY = Math.random() * varianceY - varianceY / 2 - NOTE_SIZE / 2;
            const rawNoteY = isStoryboard?
                toCanvasY(template, height * posY / pixelHeight + randomY)
                : height * posY / pixelHeight + randomY;
            const noteY = Math.max(-NOTE_SIZE/2, Math.min(rawNoteY, height - NOTE_SIZE/2));

            const newUuid = uuidv4();

            const redoEffect = async () => await createNote(presentationId, newUuid, noteX, noteY, newText, newColor)
                .catch(error => dispatch(reportError(error)));
            const undoEffect = async () => await deleteNote(presentationId, newUuid)
                .catch(error => dispatch(reportError(error)));

            dispatch({
                type: 'CREATE_NOTE',
                uuid: newUuid,
                text: newText,
                color: newColor,
                noteType: getRandomType(),
                noteX,
                noteY,
                redoEffect,
                undoEffect,
            });

            await redoEffect();
        }
    };
}

export function textareaChanged(text: string): TextAreaChangedAction {
    return {
        type: 'TEXTAREA_CHANGED',
        text,
    };
}

export function colorChanged(color: NoteColor): ColorChangedAction {
    return {
        type: 'COLOR_CHANGED',
        color,
    };
}


// WEBPACK FOOTER //
// ./src/actions/note-dialog.ts